import {
  Box,
  BoxProps,
  ThemingProps,
  theme,
  useColorMode,
  useMultiStyleConfig,
} from '@chakra-ui/react'
import {
  ComponentProps,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import Select, { SelectInstance } from 'react-select'

import {
  ClearIndicator,
  Control,
  DropdownIndicator,
  IndicatorSeparator,
  IndicatorsContainer,
  Menu,
  MenuList,
  Option,
  Placeholder,
  SelectContainer,
  SelectInputOption,
  SelectInputStylesProvider,
  SingleValue,
  ValueContainer,
  rootSelectPropsContext,
} from './SelectInput.parts'

/** Filter mode for searchable select: subsequence (chars in order) or substring (consecutive chars only) */
export type SelectFilterMode = 'subsequence' | 'substring'

function matchSubsequence(label: string, query: string): boolean {
  const queryLower = query.toLowerCase()
  const labelLower = label.toLowerCase()
  let queryIndex = 0
  for (let i = 0; i < labelLower.length; i++) {
    if (labelLower[i] === queryLower[queryIndex]) {
      queryIndex++
      if (queryIndex === queryLower.length) return true
    }
  }
  return false
}

function matchSubstring(label: string, query: string): boolean {
  return label.toLowerCase().includes(query.toLowerCase())
}

export type SelectInputProps = Pick<ThemingProps<'SelectInput'>, 'variant'> &
  Omit<BoxProps, 'variant' | 'onChange'> & {
    isClearable?: boolean
    isSearchable?: boolean
    /** How to filter options when searching: subsequence (chars in order) or substring (consecutive only) */
    filterMode?: SelectFilterMode
    /** When true, sort options by match relevance (exact match first, then by position, then alphabetical) */
    sortByMatch?: boolean
    menuWidth?: string
    maxVisibleOptions?: number
    options?: SelectInputOption[]
    onChange?: (newValue: SelectInputOption | null) => void
    defaultOptionIndex?: number
    minMenuHeight?: number
    placeholder?: string
    /** When set, the menu portal gets this data-click-boundary so parent click-outside logic doesn't close (e.g. filter editor). */
    menuBoundaryId?: string
    /** When true, Enter key with menu open stops propagation so a parent (e.g. filter editor modal) does not also handle Enter. */
    stopEnterPropagationWhenMenuOpen?: boolean
  }

export type SelectInputRef = { focus: () => void }

export const SelectInput = forwardRef<
  SelectInputRef,
  SelectInputProps & { options?: SelectInputOption[] } & Pick<
      ComponentProps<typeof Select<SelectInputOption>>,
      | 'value'
      | 'menuIsOpen'
      | 'defaultValue'
      | 'onChange'
      | 'isDisabled'
      | 'onMenuOpen'
      | 'onMenuClose'
    >
>(function SelectInput(
  {
    isClearable,
    isSearchable,
    filterMode = 'subsequence',
    sortByMatch = false,
    variant = 'primary',
    options = [],
    placeholder = 'Select',
    onChange = () => {},
    value,
    menuIsOpen,
    menuWidth,
    maxVisibleOptions,
    minMenuHeight = 300,
    defaultValue,
    isDisabled,
    onMenuOpen,
    onMenuClose,
    menuBoundaryId,
    stopEnterPropagationWhenMenuOpen,
    ...props
  },
  ref,
) {
  const { colorMode } = useColorMode()
  const rawStyles = useMultiStyleConfig('SelectInput', { variant })
  const styles = useMemo(() => {
    if (colorMode !== 'dark') return rawStyles
    const merged: Record<string, Record<string, unknown>> = {}
    for (const key of Object.keys(rawStyles)) {
      const part = rawStyles[key] as Record<string, unknown> | undefined
      if (part && typeof part === 'object' && part._dark) {
        const { _dark, ...rest } = part
        merged[key] = { ...rest, ...(_dark as Record<string, unknown>) }
      } else {
        merged[key] = (part ?? {}) as Record<string, unknown>
      }
    }
    return merged as typeof rawStyles
  }, [rawStyles, colorMode])

  const innerRef = useRef<SelectInstance<SelectInputOption>>(null)
  const [inputValue, setInputValue] = useState('')
  // Track the label set on menu open so filterOption can show all options until the user edits
  const initialInputRef = useRef('')

  useImperativeHandle(ref, () => ({
    focus: () => innerRef.current?.focus(),
  }))

  const sortedOptions = useMemo(() => {
    if (!sortByMatch) return options
    const query = inputValue.trim().toLowerCase()
    if (!query) return options
    return [...options].sort((a, b) => {
      const aLabel = (
        typeof a.label === 'string' ? a.label : String(a.label)
      ).toLowerCase()
      const bLabel = (
        typeof b.label === 'string' ? b.label : String(b.label)
      ).toLowerCase()
      const aExact = aLabel === query
      const bExact = bLabel === query
      if (aExact !== bExact) return aExact ? -1 : 1
      const aPos = aLabel.indexOf(query)
      const bPos = bLabel.indexOf(query)
      if (aPos !== bPos) return aPos - bPos
      return aLabel.localeCompare(bLabel, undefined, { sensitivity: 'base' })
    })
  }, [sortByMatch, options, inputValue])

  return (
    <Box {...props}>
      <rootSelectPropsContext.Provider
        value={{ ...props, maxVisibleOptions, menuBoundaryId }}
      >
        <SelectInputStylesProvider value={{ ...styles, rootProps: props }}>
          <Select<SelectInputOption>
            ref={innerRef}
            options={sortedOptions}
            placeholder={placeholder}
            menuPortalTarget={document.body}
            filterMode={filterMode}
            stopEnterPropagationWhenMenuOpen={stopEnterPropagationWhenMenuOpen}
            components={{
              ClearIndicator,
              SelectContainer,
              Control,
              ValueContainer,
              Placeholder,
              IndicatorSeparator,
              IndicatorsContainer,
              DropdownIndicator,
              SingleValue,
              Menu,
              MenuList,
              Option,
            }}
            styles={{
              menuPortal: base => ({
                ...base,
                zIndex: theme.zIndices.popover,
                ...(menuWidth && { width: menuWidth }),
              }),
              input: base => ({
                ...base,
                margin: 0,
                padding: 0,
                gridArea: '1/1/2/3',
                ...(colorMode === 'dark'
                  ? { color: 'var(--chakra-colors-presets-neutral-0)' }
                  : {}),
              }),
              // Keep selected value text same color when menu is open (react-select dims it by default)
              singleValue: (base, state) => ({
                ...base,
                opacity: 1,
                ...(colorMode === 'dark'
                  ? { color: 'var(--chakra-colors-presets-neutral-0)' }
                  : {}),
              }),
            }}
            isClearable={isClearable}
            isSearchable={isSearchable}
            isDisabled={isDisabled}
            value={value}
            defaultValue={defaultValue}
            inputValue={inputValue}
            onChange={(newValue, meta) => {
              setInputValue('')
              onChange(newValue)
            }}
            onInputChange={(val, meta) => {
              if (meta.action === 'input-change') {
                // Once user edits, clear the initial label so filtering kicks in
                if (
                  initialInputRef.current &&
                  val !== initialInputRef.current
                ) {
                  initialInputRef.current = ''
                }
                setInputValue(val)
              } else if (
                meta.action === 'set-value' ||
                meta.action === 'menu-close'
              ) {
                setInputValue('')
                initialInputRef.current = ''
              }
            }}
            onMenuOpen={() => {
              // Populate input with selected value's label so cursor placement works
              const selectedLabel =
                value && typeof value === 'object' && 'label' in value
                  ? (value as SelectInputOption).label
                  : undefined
              if (selectedLabel) {
                setInputValue(selectedLabel)
                initialInputRef.current = selectedLabel
              }
              onMenuOpen?.()
            }}
            onMenuClose={() => {
              setInputValue('')
              initialInputRef.current = ''
              onMenuClose?.()
            }}
            menuPlacement="auto"
            menuPosition="fixed"
            minMenuHeight={minMenuHeight}
            filterOption={(option, query) => {
              if (!query) return true
              // Don't filter when input still matches the value set on menu open
              if (initialInputRef.current && query === initialInputRef.current)
                return true
              const label =
                typeof option.label === 'string'
                  ? option.label
                  : String(option.label)
              return filterMode === 'substring'
                ? matchSubstring(label, query)
                : matchSubsequence(label, query)
            }}
          />
        </SelectInputStylesProvider>
      </rootSelectPropsContext.Provider>
    </Box>
  )
})
